import React from 'react'
import { useAppDispatch } from '@SonarCX/hooks/redux'

import EditUsersList from '@SonarCX/components/molecules/EditUsersList'
import UserListItem from './UserListItem'

import { arraysEqual } from '@SonarCX/utilities'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'
import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'
import useStyles from './styles'

type UsersListProps = {
  prop: 'assignee_ids' | 'watcher_ids'
  taskID: string
  list: string[]
  observerId: string
}

const UsersList: React.FC<UsersListProps> = ({
  prop,
  taskID,
  list,
  observerId
}) => {
  const dispatch = useAppDispatch()

  const { allUsers, loadUser } = useUserAPI()
  const { tasks, loadTask, formatTask, updateTask, setSkipUpdateID } =
    useTaskAPI()

  const task = React.useMemo(() => tasks[taskID], [tasks, taskID])
  const [ids, setIDs] = React.useState<string[]>(list)
  const [intersect, setIntersect] = React.useState(null)
  const [element, setElement] = React.useState(null)
  const updatedTask = React.useMemo(
    () => ({ ...task, [prop]: ids }),
    [task, prop, ids]
  )
  const oldIDs = React.useRef(ids)

  const classes = useStyles()

  // Load task if needed
  React.useEffect(() => {
    if (!task?.id) {
      loadTask(taskID)
    }
  }, [loadTask, taskID, task])

  React.useEffect(() => {
    if (!task?.[prop]) {
      return
    }

    // Update stored ids if they change somewhere
    if (!arraysEqual(oldIDs.current, task[prop])) {
      setIDs(task[prop])
      oldIDs.current = task[prop]
    }
  }, [task, prop])

  // Load users when needed
  React.useEffect(() => {
    const toLoad = ids.filter(id => {
      const user = allUsers[id]
      return !Boolean(user)
    })
    loadUser(toLoad)
  }, [loadUser, ids, allUsers])

  // Remove a specific id from the list
  const handleDelete = React.useCallback(
    async (id: string) => {
      try {
        const newIDs = updatedTask[prop].filter(newID => newID !== id)

        setSkipUpdateID(old => [...old, updatedTask.id])
        const newTask: LocalTask = {
          ...formatTask(updatedTask),
          [prop]: newIDs
        }
        setIDs(newIDs)

        updateTask(newTask.id, newTask)
        dispatch(AddSuccessAlert('Changes saved'))
      } catch (err) {
        dispatch(AddErrorAlert({ err }))
      }
    },
    [updatedTask, prop, formatTask, updateTask, dispatch, setSkipUpdateID]
  )

  // observer variable to set viewport and check if user is in it
  const observer = React.useRef(
    new IntersectionObserver(
      entries => {
        setIntersect(entries[0])
      },
      { root: document.querySelector(`#${observerId}`), threshold: 0 }
    )
  )

  // effect for watching and unwatching observer variable
  React.useEffect(() => {
    const currentElement = element
    const currentObserver = observer?.current

    if (currentElement) {
      currentObserver.observe(currentElement)
    }

    // clean up observer to stop watching
    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement)
      }
    }
  }, [element])

  if (ids.length === 0) {
    return <EditUsersList prop={prop} task={updatedTask} onChange={setIDs} />
  }

  return (
    <div className={classes.root} ref={setElement}>
      {ids.map(id => {
        const user = allUsers[id]
        if (!user) {
          return null
        }

        return (
          <UserListItem
            key={id}
            user={user}
            onDelete={() => handleDelete(id)}
            intersect={intersect}
          />
        )
      })}

      <EditUsersList prop={prop} task={updatedTask} onChange={setIDs} />
    </div>
  )
}

export default React.memo(UsersList)
